/* Klaviyo email capture (#106)
   ---------------------------------------------------------------------------
   Shared by the home page popup (sections/email-popup.liquid) and the footer
   newsletter form (sections/footer.liquid), so both land on the same list and
   behave the same way.

   Subscribing uses Klaviyo's PUBLIC client-side subscriptions endpoint. It is
   authenticated by the site's public company id (R7msUM) - the same id that
   appears in Klaviyo's own onsite script on every store that uses them. It is
   NOT a secret and is safe in theme code; the private API key must never
   appear here or anywhere else in the repo.

   The endpoint returns 202 with an EMPTY body on success, so success is judged
   by response.ok rather than by parsing anything out of it.

   Consent: the list is single opt-in, so a successful call subscribes the
   profile immediately. The consent wording next to each form is therefore the
   actual consent record - do not remove it. */
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  /* Pinned rather than floating: Klaviyo's API is versioned by date and an
     unpinned request follows whatever is newest, which is how a working form
     silently breaks months later. Verified against this revision - note that
     the profile object here accepts email and properties only; passing a
     `subscriptions` block is rejected as an invalid field. */
  var REVISION = '2024-10-15';

  function subscribe(opts) {
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.source || 'Website',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: opts.email }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: opts.listId } }
        }
      }
    };

    if (opts.firstName) {
      body.data.attributes.profile.data.attributes.first_name = opts.firstName;
    }

    return fetch(ENDPOINT + '?company_id=' + encodeURIComponent(opts.companyId), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return true;
      /* Surface the real reason in the console for whoever is debugging, but
         never in the UI - Klaviyo's messages are developer-facing. */
      return res.text().then(function (t) {
        throw new Error('Klaviyo ' + res.status + ': ' + t);
      });
    });
  }

  /* Basic shape check only. Real validation is the server's job; this exists so
     an obvious typo gets an instant answer instead of a round trip. */
  function looksLikeEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  /* ---------------------------------------------------------------------
     Footer newsletter - progressive enhancement.
     The markup stays a real Shopify {% form 'customer' %}. With JS we take
     over the submit and send it to Klaviyo instead; with JS broken or blocked
     the native submit still captures the address in Shopify, so the form is
     never a dead end.
     --------------------------------------------------------------------- */
  function initFooterForm(form) {
    var companyId = form.getAttribute('data-kl-company');
    var listId = form.getAttribute('data-kl-list');
    if (!companyId || !listId) return;

    var email = form.querySelector('input[type="email"]');
    var first = form.querySelector('input[name="contact[first_name]"]');
    var button = form.querySelector('button[type="submit"]');
    var status = form.querySelector('[data-kl-status]');
    if (!email || !button || !status) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.hasAttribute('data-kl-busy')) return;

      if (!looksLikeEmail(email.value)) {
        status.textContent = 'Please enter a valid email address.';
        status.hidden = false;
        email.focus();
        return;
      }

      form.setAttribute('data-kl-busy', '');
      button.disabled = true;
      status.hidden = true;

      subscribe({
        email: email.value.trim(),
        firstName: first ? first.value.trim() : '',
        companyId: companyId,
        listId: listId,
        source: form.getAttribute('data-kl-source') || 'Footer newsletter'
      }).then(function () {
        form.setAttribute('data-kl-done', '');
        status.textContent = form.getAttribute('data-kl-success') || 'You are in. Keep an eye on your inbox.';
        status.hidden = false;
        try { localStorage.setItem('sn_kl_subscribed', '1'); } catch (err) {}
      }).catch(function (err) {
        if (window.console) console.error(err);
        status.textContent = 'Something went wrong. Please try again.';
        status.hidden = false;
      }).then(function () {
        form.removeAttribute('data-kl-busy');
        button.disabled = false;
      });
    });
  }

  /* ---------------------------------------------------------------------
     Home page popup
     --------------------------------------------------------------------- */
  function initPopup(root) {
    var overlay = root.querySelector('[data-popup-overlay]');
    var panel = root.querySelector('[data-popup-panel]');
    var form = root.querySelector('[data-popup-form]');
    var email = root.querySelector('[data-popup-email]');
    var status = root.querySelector('[data-popup-status]');
    var success = root.querySelector('[data-popup-success]');
    var button = form ? form.querySelector('button[type="submit"]') : null;
    if (!overlay || !panel || !form || !email || !button) return;

    var companyId = root.getAttribute('data-kl-company');
    var listId = root.getAttribute('data-kl-list');
    var delay = parseInt(root.getAttribute('data-delay'), 10);
    var suppressDays = parseInt(root.getAttribute('data-suppress-days'), 10);
    if (isNaN(delay)) delay = 6;
    if (isNaN(suppressDays)) suppressDays = 14;

    /* In the theme editor the popup must always appear, or it cannot be styled
       - the frequency cap would hide it after the first look. */
    var designMode = !!(window.Shopify && window.Shopify.designMode);

    /* ?popup=1 forces it open immediately and does NOT record the view, so it
       can be shown and re-shown on demand. Without this the only way to see it
       a second time is clearing localStorage by hand - and because exit intent
       fires whenever the cursor leaves the top of the window (switching apps
       does it), the first view is easily missed and then suppressed for a
       fortnight. */
    var force = /[?&]popup=1(&|$)/.test(window.location.search);
    var lastFocus = null;
    var openTimer = null;

    function seenRecently() {
      if (designMode) return false;
      try {
        if (localStorage.getItem('sn_kl_subscribed')) return true;
        var t = parseInt(localStorage.getItem('sn_popup_seen'), 10);
        if (!t) return false;
        return (Date.now() - t) < suppressDays * 864e5;
      } catch (err) {
        /* Private browsing can throw on localStorage. Failing closed here would
           mean showing the popup on every single page view, which is worse for
           the visitor than never showing it. */
        return true;
      }
    }

    function markSeen() {
      if (force) return;
      try { localStorage.setItem('sn_popup_seen', String(Date.now())); } catch (err) {}
    }

    /* Keep tabbing inside the dialog while it is open. Without this, focus
       walks off into the page behind the scrim, which for a keyboard or screen
       reader user means the popup is effectively a trap they cannot see. */
    function focusables() {
      return Array.prototype.filter.call(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open() {
      if (root.hasAttribute('data-open')) return;
      lastFocus = document.activeElement;
      root.setAttribute('data-open', '');
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);
      markSeen();
      var target = email;
      /* Focus the panel rather than the field on touch devices - focusing an
         input there yanks the keyboard up over the design the moment it opens. */
      if (window.matchMedia('(hover: none)').matches) target = panel;
      window.setTimeout(function () { try { target.focus(); } catch (err) {} }, 80);
    }

    function close() {
      if (!root.hasAttribute('data-open')) return;
      root.removeAttribute('data-open');
      overlay.classList.remove('open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-popup-close]'), function (el) {
      el.addEventListener('click', close);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.hasAttribute('data-busy')) return;

      if (!looksLikeEmail(email.value)) {
        status.textContent = 'Please enter a valid email address.';
        status.hidden = false;
        email.focus();
        return;
      }

      form.setAttribute('data-busy', '');
      button.disabled = true;
      status.hidden = true;

      subscribe({
        email: email.value.trim(),
        companyId: companyId,
        listId: listId,
        source: root.getAttribute('data-kl-source') || 'Home page popup'
      }).then(function () {
        try { localStorage.setItem('sn_kl_subscribed', '1'); } catch (err) {}

        /* Reveal the success line BEFORE flipping the state attribute, and force a reflow in
           between. Un-hiding and adding the class in the same frame gives the browser no
           starting value to animate from, so it would simply appear. */
        if (success) {
          success.hidden = false;
          void success.offsetWidth;
        }
        root.setAttribute('data-success', '');

        /* Once the form has faded and lifted away, take it out of the layout so the heading and
           message can actually grow into the space it was using. Matches the CSS duration. */
        window.setTimeout(function () { form.hidden = true; }, 340);
      }).catch(function (err) {
        if (window.console) console.error(err);
        status.textContent = 'Something went wrong. Please try again.';
        status.hidden = false;
      }).then(function () {
        form.removeAttribute('data-busy');
        button.disabled = false;
      });
    });

    if (force || designMode) { open(); return; }

    if (seenRecently()) return;

    openTimer = window.setTimeout(open, delay * 1000);

    /* Exit intent as a second chance: if the cursor leaves through the top of
       the window before the timer fires, show it then instead. Pointer-based,
       so it never triggers on touch where there is no cursor to leave. */
    if (root.getAttribute('data-exit-intent') === 'true') {
      document.addEventListener('mouseout', function onOut(e) {
        if (e.clientY > 0 || e.relatedTarget) return;
        document.removeEventListener('mouseout', onOut);
        window.clearTimeout(openTimer);
        open();
      });
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-kl-form]'), initFooterForm);
    var popup = document.querySelector('[data-email-popup]');
    if (popup) initPopup(popup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init when the theme editor swaps a section back in, otherwise the popup
     stops responding as soon as someone edits it. */
  document.addEventListener('shopify:section:load', init);
})();
